import { Card, SkeletonLine } from '../../components/Card';
import { IconButton, Pill } from '../../components/ui';
import { clsx } from '../../components/clsx';
import { formatShortDay, parseLocalDateKey } from '../../lib/date';
import type { DayGroup } from '../../lib/google/events';
import { useGoogleCalendar } from '../../lib/google/useGoogleCalendar';
import { ConnectPrompt } from './ConnectPrompt';
import { EventRow } from './EventRow';

/**
 * Calendar: today plus the next two days, colour-coded by calendar.
 *
 * ROW BUDGET. The card is ~350x330 usable at its smallest measured size, and
 * three day headers plus their rows must fit with no card overflow and no
 * page scroll. Today gets 4 rows because it is the day you act on; the two
 * lookahead days get 2 each. Anything beyond that collapses into a "+N" chip
 * rather than being dropped silently.
 */
const ROW_CAPS: readonly number[] = [4, 2, 2];

export function CalendarPanel() {
  const { status, days, error, connect, disconnect, refresh } = useGoogleCalendar();

  const connected = status === 'ready' || status === 'loading' || status === 'error';
  const totalEvents = days.reduce((n, day) => n + day.events.length, 0);

  return (
    <Card
      title="Calendar"
      icon="📅"
      accent="cal"
      testId="calendar-panel"
      status={status === 'error' ? 'error' : 'ready'}
      badge={
        error !== null && status !== 'error' ? (
          <Pill tone="warn" testId="calendar-stale">
            stale
          </Pill>
        ) : undefined
      }
      action={
        connected ? (
          <>
            <IconButton label="Refresh calendar" onClick={refresh}>
              ⟳
            </IconButton>
            <IconButton label="Disconnect Google Calendar" onClick={disconnect}>
              ⏻
            </IconButton>
          </>
        ) : undefined
      }
      errorMessage={error ?? "Couldn't read your calendar."}
      onRetry={refresh}
      stateAction={
        <button
          type="button"
          onClick={disconnect}
          className="text-[11px] text-ink-subtle underline underline-offset-2 hover:text-ink"
        >
          Disconnect
        </button>
      }
    >
      {status === 'unconfigured' || status === 'disconnected' || status === 'connecting' ? (
        <ConnectPrompt
          configured={status !== 'unconfigured'}
          connecting={status === 'connecting'}
          error={error}
          onConnect={connect}
        />
      ) : status === 'loading' ? (
        <CalendarSkeleton />
      ) : (
        <div className="flex h-full min-h-0 flex-col gap-1.5" data-testid="calendar-days">
          {days.map((day, index) => (
            <DaySection key={day.dateKey} day={day} index={index} cap={ROW_CAPS[index] ?? 2} />
          ))}
          {totalEvents === 0 && (
            <p className="mt-auto text-center text-[11px] text-ink-subtle">
              Nothing scheduled for the next three days.
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

const DAY_TITLES: readonly string[] = ['Today', 'Tomorrow'];

function DaySection({ day, index, cap }: { day: DayGroup; index: number; cap: number }) {
  const shown = day.events.slice(0, cap);
  const hidden = day.events.length - shown.length;
  const parsed = parseLocalDateKey(day.dateKey);

  return (
    <section className="min-h-0">
      <div className="mb-0.5 flex items-baseline gap-1.5 border-b border-line pb-0.5">
        <h3
          className={clsx(
            'text-[10px] font-semibold uppercase tracking-wide',
            index === 0 ? 'text-accent-cal' : 'text-ink-muted',
          )}
        >
          {DAY_TITLES[index] ?? (parsed === null ? day.dateKey : formatShortDay(parsed))}
        </h3>
        <span className="text-[10px] tabular-nums text-ink-subtle">
          {parsed === null ? '' : `${formatShortDay(parsed)} ${parsed.getDate()}`}
        </span>
        {hidden > 0 && (
          <span className="ml-auto shrink-0 rounded-full bg-panel-raised px-1.5 text-[9px] font-semibold text-ink-subtle">
            +{hidden} more
          </span>
        )}
      </div>

      {shown.length === 0 ? (
        <p className="py-[2px] text-[11px] italic text-ink-subtle">Clear</p>
      ) : (
        shown.map((event) => <EventRow key={event.key} event={event} />)
      )}
    </section>
  );
}

function CalendarSkeleton() {
  return (
    <div className="flex flex-col gap-2.5 pt-1">
      {ROW_CAPS.map((rows, section) => (
        <div key={section} className="flex flex-col gap-1.5">
          <SkeletonLine w="w-20" h="h-2.5" />
          {Array.from({ length: rows }, (_, i) => (
            <SkeletonLine key={i} w={i % 2 === 0 ? 'w-full' : 'w-4/5'} />
          ))}
        </div>
      ))}
    </div>
  );
}
